import type {
  AdminPasswordRecoveryResult,
  AuthUser,
  DeviceSummary,
  MfaCredential,
  MfaEnrollment,
  MfaMethod,
  PasswordChangeResult,
  ProviderPasswordRecoveryRequest,
  ProviderPasswordRecoveryRequestResult,
  ProviderPasswordRecoveryResetRequest,
  ProviderPasswordRecoveryResult
} from "@kuroo/contracts";
import type { LoginResult, SessionSummary } from "./client-auth-types";

export type SdkAuthContext = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export function login(context: SdkAuthContext, email: string, password: string): Promise<LoginResult> {
  return context.request<LoginResult>("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
}

export function changePassword(context: SdkAuthContext, input: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<PasswordChangeResult> {
  return context.request<PasswordChangeResult>("/api/v1/auth/password/change", { method: "POST", body: JSON.stringify(input) });
}

export function requestProviderPasswordRecovery(context: SdkAuthContext, email: string): Promise<ProviderPasswordRecoveryRequestResult> {
  const input: ProviderPasswordRecoveryRequest = { email };
  return context.request<ProviderPasswordRecoveryRequestResult>("/api/v1/auth/password/provider/request", { method: "POST", body: JSON.stringify(input) });
}

export function completeProviderPasswordRecovery(context: SdkAuthContext, input: ProviderPasswordRecoveryResetRequest): Promise<ProviderPasswordRecoveryResult> {
  return context.request<ProviderPasswordRecoveryResult>("/api/v1/auth/password/provider/reset", { method: "POST", body: JSON.stringify(input) });
}

export function recoverAdminPassword(context: SdkAuthContext, input: { email: string; newPassword: string; confirmPassword: string; newEmail?: string }, recoveryToken: string): Promise<AdminPasswordRecoveryResult> {
  return context.request<AdminPasswordRecoveryResult>("/api/v1/auth/password/recover-admin", { method: "POST", body: JSON.stringify(input), headers: { "X-Admin-Recovery-Token": recoveryToken } });
}

export function mfaCredentials(context: SdkAuthContext): Promise<MfaCredential[]> {
  return context.request<MfaCredential[]>("/api/v1/auth/mfa");
}

export function enrollMfa(context: SdkAuthContext, input: { method: MfaMethod; label?: string }): Promise<MfaEnrollment & { challengeId: string; expiresAt: string; recoveryCodes?: string[] }> {
  return context.request<MfaEnrollment & { challengeId: string; expiresAt: string; recoveryCodes?: string[] }>("/api/v1/auth/mfa/enroll", { method: "POST", body: JSON.stringify(input) });
}

export function verifyMfa(context: SdkAuthContext, input: { challengeId: string; challenge: string; code?: string; assertion?: Record<string, unknown> }): Promise<AuthUser> {
  return context.request<AuthUser>("/api/v1/auth/mfa/verify", { method: "POST", body: JSON.stringify(input) });
}

export function recoverMfa(context: SdkAuthContext, input: { challengeId: string; code: string }): Promise<AuthUser> {
  return context.request<AuthUser>("/api/v1/auth/mfa/recovery", { method: "POST", body: JSON.stringify(input) });
}

export function disableMfa(context: SdkAuthContext, password: string): Promise<{ disabled: boolean }> {
  return context.request<{ disabled: boolean }>("/api/v1/auth/mfa/disable", { method: "POST", body: JSON.stringify({ password }) });
}

export function sessions(context: SdkAuthContext): Promise<SessionSummary[]> {
  return context.request<SessionSummary[]>("/api/v1/auth/sessions");
}

export function revokeSession(context: SdkAuthContext, sessionId: string): Promise<{ id: string; revoked: boolean; current: boolean }> {
  return context.request<{ id: string; revoked: boolean; current: boolean }>(`/api/v1/auth/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
}

export function revokeOtherSessions(context: SdkAuthContext): Promise<{ revoked: number; current: boolean }> {
  return context.request<{ revoked: number; current: boolean }>("/api/v1/auth/sessions", { method: "DELETE", body: "{}" });
}

export function devices(context: SdkAuthContext): Promise<DeviceSummary[]> {
  return context.request<DeviceSummary[]>("/api/v1/auth/devices");
}

export function revokeDevice(context: SdkAuthContext, deviceId: string): Promise<{ id: string; revoked: boolean; current: boolean }> {
  return context.request<{ id: string; revoked: boolean; current: boolean }>(`/api/v1/auth/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE" });
}

export function logout(context: SdkAuthContext): Promise<{ loggedOut: boolean }> {
  return context.request<{ loggedOut: boolean }>("/api/v1/auth/logout", { method: "POST", body: "{}" });
}
